import React, { useState, useEffect } from 'react';
import { Nomina } from '../../services/nomina/nomina.service';
import { usePermissions } from '../../hooks/usePermissions';
import {
  X, User, Calendar, Banknote, CheckCircle, Clock,
  Trash2, DollarSign, BadgePercent, ArrowUpCircle,
  ArrowDownCircle, Sun, AlertCircle, PartyPopper,
  TrendingUp, TrendingDown
} from 'lucide-react';

interface Props {
  nomina: Nomina;
  onClose: () => void;
  onPagar: (id: string) => void;
  onDelete: (id: string) => void;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);

const formatDate = (dateStr: string) => {
  const fecha = dateStr.match(/^\d{4}-\d{2}-\d{2}/)?.[0] || dateStr;
  return new Date(`${fecha}T00:00:00-05:00`).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
};

const novedadConfig: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  FALTA:            { label: 'Falta',            color: 'text-red-700',      bg: 'bg-red-100',      icon: <AlertCircle className="w-3.5 h-3.5" /> },
  HORA_EXTRA:       { label: 'Hora extra',       color: 'text-blue-700',     bg: 'bg-blue-100',     icon: <Clock className="w-3.5 h-3.5" /> },
  BONIFICACION:     { label: 'Bonificación',     color: 'text-emerald-700',  bg: 'bg-emerald-100',  icon: <TrendingUp className="w-3.5 h-3.5" /> },
  FESTIVO:          { label: 'Festivo',          color: 'text-amber-700',    bg: 'bg-amber-100',    icon: <PartyPopper className="w-3.5 h-3.5" /> },
  DESCUENTO_OTRO:   { label: 'Descuento',        color: 'text-rose-700',     bg: 'bg-rose-100',     icon: <TrendingDown className="w-3.5 h-3.5" /> },
};

export const NominaDetailDrawer: React.FC<Props> = ({ nomina, onClose, onPagar, onDelete }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  const devengado = nomina.totalDevengado;
  const descuentos = nomina.totalDescuentos;
  const bonificaciones = nomina.totalBonificaciones;
  // Base quincenal fija, las bonificaciones ya no incluyen dominicales
  const base = devengado - bonificaciones;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />
      <div
        className={`fixed right-0 top-20 h-[calc(100vh-5rem)] w-full max-w-2xl bg-gray-50 shadow-2xl z-40 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Banknote className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Detalle de Quincena</h2>
                <p className="text-sm text-gray-500">{formatDate(nomina.periodoInicio)} — {formatDate(nomina.periodoFin)}</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Colaborador */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-emerald-500" />
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Colaborador</h3>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-gray-100">
                  <User className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">{nomina.usuario?.nombreCompleto || '—'}</p>
                  <p className="text-sm text-gray-500">{nomina.usuario?.email || '—'}</p>
                </div>
              </div>
            </div>

            {/* Estado */}
            <div className={`p-4 rounded-xl border-2 ${
              nomina.estado === 'PAGADA'
                ? 'bg-green-50 border-green-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${nomina.estado === 'PAGADA' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                  {nomina.estado === 'PAGADA' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <Clock className="w-5 h-5 text-yellow-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Estado</p>
                  <p className={`font-bold ${nomina.estado === 'PAGADA' ? 'text-green-700' : 'text-yellow-700'}`}>
                    {nomina.estado === 'PAGADA' ? 'Pagada' : 'Pendiente de pago'}
                  </p>
                </div>
              </div>
            </div>

            {/* Condiciones */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <BadgePercent className="w-4 h-4 text-emerald-500" />
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Condiciones Laborales</h3>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-center">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Salario mensual</p>
                  <p className="text-sm font-bold text-gray-900">{formatCurrency(nomina.salarioMensual)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-center">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Días laborados</p>
                  <p className="text-sm font-bold text-gray-900">{nomina.diasLaborados}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-center">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Domingos</p>
                  <p className="text-sm font-bold text-gray-900">{nomina.domingosTrabajados}</p>
                </div>
              </div>
            </div>

            {/* Resumen de Pago */}
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5">
              <h3 className="text-sm font-bold text-emerald-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Banknote className="w-4 h-4" />
                Resumen de Pago
              </h3>

              <div className="text-center p-4 bg-white rounded-xl border border-emerald-200 mb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Total a pagar</p>
                <p className="text-3xl font-black text-emerald-700">{formatCurrency(nomina.totalPagar)}</p>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-100">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-gray-100 rounded-md"><Calendar className="w-3.5 h-3.5 text-gray-500" /></div>
                    <span className="text-xs font-medium text-gray-600">Base quincenal</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(base)}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-100">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-emerald-100 rounded-md"><ArrowUpCircle className="w-3.5 h-3.5 text-emerald-600" /></div>
                    <span className="text-xs font-medium text-gray-600">Bonificaciones (festivos, horas extra, etc.)</span>
                  </div>
                  <span className="text-sm font-bold text-emerald-700">+{formatCurrency(bonificaciones)}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-100">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-red-100 rounded-md"><ArrowDownCircle className="w-3.5 h-3.5 text-red-600" /></div>
                    <span className="text-xs font-medium text-gray-600">Descuentos</span>
                  </div>
                  <span className="text-sm font-bold text-red-600">-{formatCurrency(descuentos)}</span>
                </div>
              </div>

              <div className="pt-3 border-t border-emerald-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total devengado</span>
                  <span className="text-base font-bold text-emerald-900">{formatCurrency(devengado)}</span>
                </div>
              </div>
            </div>

            {/* Novedades */}
            {nomina.novedades.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <BadgePercent className="w-4 h-4 text-emerald-500" />
                  Novedades ({nomina.novedades.length})
                </h3>
                <div className="space-y-2">
                  {nomina.novedades.map((n) => {
                    const cfg = novedadConfig[n.tipo] || novedadConfig.DESCUENTO_OTRO;
                    const total = n.valorUnitario * n.cantidad;
                    const isDescuento = n.tipo === 'FALTA' || n.tipo === 'DESCUENTO_OTRO';
                    return (
                      <div key={n.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${cfg.bg} ${cfg.color}`}>
                            {cfg.icon}
                            {cfg.label}
                          </span>
                          <span className="text-xs text-gray-600">{n.descripcion || n.tipo}</span>
                        </div>
                        <span className={`text-xs font-bold ${isDescuento ? 'text-red-600' : 'text-emerald-600'}`}>
                          {isDescuento ? '-' : '+'}{formatCurrency(total)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Observaciones */}
            {nomina.observaciones && (
              <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-4">
                <h4 className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-1">Observaciones</h4>
                <p className="text-sm text-blue-900">{nomina.observaciones}</p>
              </div>
            )}
          </div>

          {/* Footer - Acciones */}
          <div className="px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex gap-3">
              {nomina.estado === 'PENDIENTE' && hasPermission('Nomina', 'editar') && (
                <button
                  onClick={() => { onPagar(nomina.id); handleClose(); }}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <DollarSign className="w-5 h-5" />
                  Marcar como Pagada
                </button>
              )}
              {hasPermission('Nomina', 'eliminar') && (
                <button
                  onClick={() => onDelete(nomina.id)}
                  className="px-4 py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center"
                  title="Eliminar"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


